import React from 'react';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';

import withCompute from 'lib/mobx/with-compute';
import compute from 'lib/mobx/compute';
import { InterscrollerPlaceholder } from 'lib/google/dfp/ad-units/interscroller';

import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import { HideForVendor } from 'four-nets/vendor/permissions';

import { CategoryLink } from '../category-link';
import PanelGroupView, { PanelGroupViewModel } from './panel-group-view';
import defaultStyles from './panel-view.scss';

const CATEGORY_POSITION = new Map([
    [
        101,
        new Map([
            ['Prípravy na svadbu', [0, 0]],
            ['Nevesta', [1, 1]],
            ['Ženích', [0, 1]],
            ['Pred svadbou', [1, 2]],
            ['Po svadbe', [1, 3]],
            ['Sme na tom rovnako', [1, 4]],
            ['Všetko možné', [1, 5]],
        ]),
    ],
    [
        102,
        new Map([
            ['Svatební přípravy', [0, 0]],
            ['Ženich', [0, 1]],
            ['Nevěsta', [1, 0]],
            ['Před svatbou', [1, 1]],
            ['Po svatbě', [1, 2]],
            ['Žijeme v byte a dome', [1, 3]],
            ['Jsme na tom stejně', [1, 4]],
            ['Všechno možné', [1, 5]],
        ]),
    ],
    [
        301,
        new Map([
            ['Pred stavbou', [0, 0]],
            ['Rodinný dom', [0, 1]],
            ['Exteriér', [0, 2]],
            ['Žijeme v byte a dome', [0, 3]],
            ['Kúpa a financie', [1, 0]],
            ['Vnútorná stavba', [1, 1]],
            ['Zariaďujeme', [1, 2]],
            ['Byt', [1, 3]],
            ['Všetko možné', [1, 4]],
        ]),
    ],
    [
        302,
        new Map([
            ['Před stavbou', [0, 0]],
            ['Rodinný dům', [0, 1]],
            ['Exteriér', [0, 2]],
            ['Žijeme v bytě a domě', [0, 3]],
            ['Koupě a finance', [1, 0]],
            ['Interiérová stavba', [1, 1]],
            ['Zařizujeme', [1, 2]],
            ['Byt', [1, 3]],
            ['Všechno možné', [1, 4]],
        ]),
    ],
    [
        401,
        new Map([
            ['Okrasná záhrada', [0, 0]],
            ['Choroby a škodce', [0, 1]],
            ['Stavby a terénne úpravy', [0, 2]],
            ['Chováme v záhrade', [0, 3]],
            ['Úžitková záhrada', [1, 0]],
            ['Záhradná technika a náradie', [1, 1]],
            ['Balkón, terasa, interiér', [1, 2]],
            ['Všetko ostatné', [1, 3]],
        ]),
    ],
]);

export { PanelViewModel };

@withCompute()
class PanelViewModel {
    columns = null;
    @compute title;

    disposer;

    constructor(options) {
        const { categoryStore } = options;
        this.disposer = autorun(() => {
            const { categories, superCategories, site } = categoryStore;

            if (!categories && !superCategories) {
                return;
            }

            const ordering = CATEGORY_POSITION.get(site.id);
            const columnLengths = [0, 0];
            ordering.forEach(category => {
                columnLengths[category[0]]++;
            });

            const columns = [new Array(columnLengths[0]), new Array(columnLengths[1])];

            let loaded = false;

            superCategories.forEach(superCategory => {
                loaded = true;
                if (superCategory.row < 0) {
                    return;
                }

                const panelView = new PanelGroupViewModel({
                    title: superCategory.name,
                    links: superCategory.categories.map(
                        category =>
                            new CategoryLink({
                                category,
                            })
                    ),
                });

                const order = ordering.get(superCategory.name);

                if (order) {
                    const [column, row] = ordering.get(superCategory.name);
                    columns[column][row] = panelView;
                }
            });

            if (loaded) {
                this.columns = columns;
            }
        });
    }
}

@Styled(defaultStyles)
@observer
class PanelView extends React.Component {
    componentWillUnmount() {
        if (this.props.panelViewModel.diposer) {
            this.props.panelViewModel.diposer();
        }
    }

    render() {
        const { panelViewModel, styles, title, onNewThreadClick } = this.props;
        const { columns } = panelViewModel;

        return (
            <div className={styles.panel}>
                {title ? (
                    <div className={styles.titleRow}>
                        <h1 className={styles.title}>{title}</h1>
                        <HideForVendor>
                            <div className={styles.button}>
                                <BigButtonView
                                    icon={newArticle}
                                    label={<FormattedMessage id='NEW_THREAD_BUTTON' />}
                                    onClick={onNewThreadClick}
                                />
                            </div>
                        </HideForVendor>
                    </div>
                ) : null}
                <div className={styles.columns}>
                    {columns ? (
                        <React.Fragment>
                            {columns.map((column, index) => (
                                <div key={index} className={styles.column}>
                                    {column.map(panelGroupViewModel =>
                                        panelGroupViewModel ? (
                                            <PanelGroupView
                                                panelGroupViewModel={panelGroupViewModel}
                                                onClick={clickedViewModel => {
                                                    columns.forEach(column => {
                                                        column.forEach(panelViewGroup => {
                                                            if (panelViewGroup.title !== clickedViewModel.title) {
                                                                panelViewGroup.hidden = true;
                                                            } else {
                                                                clickedViewModel.hidden = !clickedViewModel.hidden;
                                                            }
                                                        });
                                                    });
                                                }}
                                                key={panelGroupViewModel.title}
                                            />
                                        ) : null
                                    )}
                                </div>
                            ))}
                        </React.Fragment>
                    ) : null}
                </div>
                <div style={{ marginTop: 10 }}>
                    <InterscrollerPlaceholder />
                </div>
            </div>
        );
    }
}

export default PanelView;
